"use client";
import { useAppDispatch, useAppSelector } from "lib/redux/hooks";
import {
  selectSettings,
  setCurrentSection,
  setTemplateWithDefaults,
} from "lib/redux/settingsSlice";
import { changeTemplateId, selectResume } from "lib/redux/resumeSlice";
import { useTemplateComponents } from "components/Resume/templateUtils";
import { cx } from "lib/cx";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Image from "next/image";
import { useState } from "react";
import { TemplatePreview } from "./TemplatePreview";
import { useTailwindBreakpoints } from "lib/hooks/useTailwindBreakpoints";

export const TemplateForm = () => {
  const resume = useAppSelector(selectResume);
  const settings = useAppSelector(selectSettings);
  const dispatch = useAppDispatch();
  const [previewTemplateId, setPreviewTemplateId] = useState<number | null>(
    null
  );
  const [isExitingPreview, setIsExitingPreview] = useState(false); // State to handle exit animation
  const [isEnteringPreview, setIsEnteringPreview] = useState(false); // State to handle entry animation
  const { isLg, isXl, is2xl } = useTailwindBreakpoints();

  const handleTemplateChange = (template: number) => {
    dispatch(changeTemplateId(template));
    dispatch(setTemplateWithDefaults(template));
  };

  // Function to handle preview button click
  const handlePreview = (templateId: number) => {
    setPreviewTemplateId(templateId); // Set the template to be previewed
    setIsEnteringPreview(true); // Trigger the fade-in-left effect
  };

  const handleBackClick = () => {
    setIsExitingPreview(true); // Trigger the exit animation
    setTimeout(() => {
      setPreviewTemplateId(null); // Remove the preview after animation
      setIsExitingPreview(false); // Reset exit state
    }, 500); // Adjust timing based on animation duration
  };

  const { templateList, template } = useTemplateComponents(resume, settings);

  const OrderedTemplateList = templateList.sort((a, b) => a.order - b.order);

  return (
    <section className="fade-in-left transition-opacity duration-200">
      {
        // If a template is selected to be previewed, show the preview
        previewTemplateId ? (
          <div
            className={cx(
              "relative transition-transform duration-500 ease-in-out",
              isExitingPreview && "fade-out-left", // Fade out to left when exiting
              isEnteringPreview && "fade-in-left" // Fade in left when entering
            )}
          >
            <div
              className="mb-1 ml-auto h-6 w-6 cursor-pointer rounded-full bg-[#4E4E4E] p-1 text-white shadow-md"
              onClick={handleBackClick}
            >
              <XMarkIcon />
            </div>
            <div>
              <TemplatePreview previewTemplateId={previewTemplateId} />
            </div>
          </div>
        ) : (
          <ul className="mx-auto grid w-fit grid-cols-2 justify-items-center gap-7 2xl:grid-cols-3">
            {OrderedTemplateList.map((templateElement) => (
              <li
                key={templateElement.id}
                className="group relative w-fit"
                onClick={() => handleTemplateChange(templateElement.id)}
              >
                <Image
                  src={templateElement.imageUrl}
                  alt={templateElement.name}
                  width={is2xl ? 271 : isXl ? 226 : isLg ? 181 : 226}
                  height={is2xl ? 352 : isXl ? 293 : isLg ? 234 : 293}
                  className={cx(
                    "shadow-lg",
                    templateElement.id === template
                      ? "border-[7px] border-[#1890FE]"
                      : ""
                  )}
                />
                {/* OVERLAY */}
                <div className="absolute inset-0 z-10 flex items-end justify-center bg-black bg-opacity-0 transition-all duration-300 ease-in-out">
                  <div className="mb-5 hidden translate-y-4 transform gap-1 text-center opacity-0 transition-all duration-300 ease-in-out group-hover:translate-y-0 group-hover:opacity-100 lg:flex xl:gap-2">
                    <button
                      className="btn-gradient px-2 py-1 font-poppins text-[10px] xl:px-6 xl:py-2 xl:text-xs"
                      onClick={() => handlePreview(templateElement.id)}
                    >
                      Preview
                    </button>
                    <button
                      onClick={() => {
                        dispatch(setCurrentSection("personalDetails"));
                        handleTemplateChange(templateElement.id);
                      }}
                      className="btn-gradient px-2 py-1 font-poppins text-[10px] xl:px-6 xl:py-2 xl:text-xs"
                    >
                      Use this
                    </button>
                  </div>
                  <div
                    className="h-full w-full lg:hidden"
                    onClick={() => handlePreview(templateElement.id)}
                  />
                </div>
              </li>
            ))}
          </ul>
        )
      }
    </section>
  );
};
